import * as React from "react"
import Grid from "@mui/material/Grid2"
import SearchIcon from '@mui/icons-material/Search';
import TimerIcon from '@mui/icons-material/Timer';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EmailIcon from '@mui/icons-material/Email';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import AddCircleSharpIcon from '@mui/icons-material/AddCircleSharp';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import {
    DataGrid,
} from "@mui/x-data-grid"
import { AXIOS_API_INSTANCE, get_service_order_color } from "../../Constants"
import { send_authorized_request } from "../../Constants/cognito"
import { formatCustomerName, formatDurationDays, formatPhoneNumber, widthChangeEvent } from "../../Utils"
import { useSnackbar } from 'notistack';
import { SimpleCustomToolbar } from "../CustomToolbar"
import onFilterChange from "../../Utils/OnFilterChange"
import { Box, CircularProgress, InputAdornment, TextField, Tooltip, debounce, Autocomplete, FormControl, Chip, Checkbox, MenuItem, InputLabel, Select, OutlinedInput, Button, Menu, Divider, ListSubheader, IconButton, Badge } from "@mui/material"
import dayjs from "dayjs"
import timezone from "dayjs/plugin/timezone";
import { useNavigate, useParams } from "react-router-dom"
import { useTrackedLocation, useTrackedUser } from "../UserState"
import ServiceOrder from "./ServiceOrder";
import UserAvatar from "../UserAvatar";
import { serviceOrderChip, sort_custom_statuses } from "../../Utils/service";
import { useUsers, build_users_list, findUser } from "../ReactQuery/QueryUsers";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import DocumentTitle from "../../Utils/DocumentTitle";
import { findLocation, useLocations } from "../ReactQuery/QueryLocations";
import CustomerSearchModal from "../modals/CustomerSearchModal";
import InputModal from "../modals/InputModal";
import ConfirmModal from "../modals/ConfirmModal";
dayjs.extend(timezone);

const columns = (my_user, users, locations) => [
    {
        field: "id",
        headerName: "#",
        width: 75,
        editable: false,
        filterable: false,
        display: 'flex',
        renderCell: ({ value }) => (
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {value}
            </Box>
        ),
    },
    {
        field: "status_name",
        headerName: "Status",
        width: 200,
        editable: false,
        filterable: false,
        display: 'flex',
        renderCell: (params) => (
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {serviceOrderChip(my_user, params.row.status_name, params.row.status_type)}
            </Box>
        )
    },
    {
        field: "customer",
        headerName: "Customer",
        minWidth: 150,
        flex: 1,
        editable: false,
        sortable: false,
        filterable: false,
        display: 'flex',
        renderCell: (params) => {
            return (
                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <span>{formatCustomerName(params.row.customer)}<br />
                        {formatPhoneNumber(params.row.customer.phone)}</span>
                </Box>
            )
        }
    },
    {
        field: "customer_item:manufacturer",
        headerName: "Manufacturer",
        minWidth: 120,
        flex: 1,
        editable: false,
        sortable: true,
        filterable: true,
        valueGetter: (value, row) => row.customer_item.manufacturer || "",
        display: 'flex',
        renderCell: ({ value }) => (
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {value}
            </Box>
        ),
    },
    {
        field: "customer_item:model",
        headerName: "Model",
        minWidth: 120,
        flex: 1,
        editable: false,
        sortable: true,
        filterable: true,
        valueGetter: (value, row) => row.customer_item.model || "",
        display: 'flex',
        renderCell: ({ value }) => (
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {value}
            </Box>
        ),
    },
    {
        field: "customer_item:identifier",
        headerName: "Identifier",
        minWidth: 120,
        flex: 1,
        editable: false,
        sortable: true,
        filterable: true,
        valueGetter: (value, row) => row.customer_item.identifier || "",
        display: 'flex',
        renderCell: ({ value }) => (
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {value}
            </Box>
        ),
    },
    {
        field: "time_in_store",
        headerName: "Time In Shop",
        type: 'datetime',
        editable: false,
        sortable: true,
        minWidth: 120,
        flex: 1,
        display: 'flex',
        renderCell: (params) => {
            let hours = Math.floor(params.value / 60 / 60)
            const days = Math.floor(hours / 24);
            hours = hours - (days * 24);
            return (
                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {my_user.client.service_order_statuses[params.row.status_name].alert_hours > 0 &&
                        dayjs().diff(dayjs.utc(params.row.last_status_change), 'hours') >= my_user.client.service_order_statuses[params.row.status_name].alert_hours &&
                        <Tooltip title={`No status change in ${formatDurationDays(dayjs().diff(dayjs.utc(params.row.last_status_change), 'hours'))}`}>
                            <InfoOutlinedIcon sx={{ height: '20px', verticalAlign: 'text-bottom' }} color="error" />
                        </Tooltip>
                    }
                    {params.row.actively_worked &&
                        <Tooltip title={`Currently Being Worked On`}>
                            <TimerIcon color="error" sx={{ height: '20px', verticalAlign: 'text-bottom' }} />
                        </Tooltip>
                    }
                    {params.row.customer_approved_status === "APPROVAL_REQUESTED" &&
                        <Tooltip title={`Customer Approval Requested`}>
                            <EmailIcon sx={{ height: '20px', verticalAlign: 'text-bottom' }} color="warning" />
                        </Tooltip>
                    }
                    {params.row.customer_approved_status === "CUSTOMER_RESPONDED" &&
                        <Tooltip title={`Customer Responded to Quote`}>
                            <EmailIcon sx={{ height: '20px', verticalAlign: 'text-bottom' }} color="success" />
                        </Tooltip>
                    }
                    <span>{days} days, {hours} hrs</span>
                </Box>
            )
        }
    },
    {
        field: "assignee_user_id",
        headerName: "Assignee",
        type: 'special?',
        editable: false,
        sortable: true,
        filterable: false,
        minWidth: 120,
        flex: 1,
        display: 'flex',
        renderCell: (params) => {
            const user = findUser(users, params.row.assignee_user_id) ?? {}
            return (
                (<Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <Grid container space={1} alignItems="center" wrap="nowrap">
                        <Grid sx={{ pr: 1 }} size="auto">
                            <span><UserAvatar user={user} /></span>
                        </Grid>
                        <Grid size="grow">
                            <span>{user?.name ?? "None"}</span>
                        </Grid>
                    </Grid>
                </Box>)
            );
        }
    },
    {
        field: "last_status_change",
        headerName: "Last Status Change",
        type: 'dateTime',
        editable: false,
        sortable: true,
        minWidth: 120,
        flex: 1,
        display: 'flex',
        valueGetter: (value, row) => dayjs.utc(value).toDate(),
    },
    {
        field: "location_id",
        headerName: "Location",
        editable: false,
        sortable: true,
        minWidth: 120,
        flex: 1,
        display: 'flex',
        renderCell: ({ value }) => (
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {value}
            </Box>
        ),
        valueGetter: (value, row) => findLocation(locations, value)?.name
    },
    {
        field: "current_location_id",
        headerName: "Current Location",
        editable: false,
        sortable: true,
        minWidth: 120,
        flex: 1,
        display: 'flex',
        renderCell: ({ value }) => (
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {value}
            </Box>
        ),
        valueGetter: (value, row) => findLocation(locations, value)?.name
    },
    {
        field: "date_created",
        headerName: "Date Created",
        type: 'datetime',
        editable: false,
        sortable: true,
        minWidth: 120,
        flex: 1,
        display: 'flex',
        renderCell: ({ value }) => (
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <span>{dayjs.utc(value).local().format("MM/DD/YYYY")}</span>
            </Box>
        ),
    },
]

const get_included_statuses = (my_user) => {
    return sort_custom_statuses(my_user.client.service_order_statuses).filter((status) => {
        return ['CREATED', 'IN_PROGRESS'].includes(my_user.client.service_order_statuses[status].type)
    })
}

export function ServiceOrderStatusFilter({ statuses, includeStatuses, setIncludeStatuses, isLoading = false, serviceOrderStats, showServiceOrderStats = true, localStorageLocation = `serviceorders:status_filters` }) {
    const [my_user] = useTrackedUser();
    const [newIncludeStatuses, setNewIncludeStatuses] = React.useState(localStorage.getItem(localStorageLocation) ? localStorage.getItem(localStorageLocation).split(',') : get_included_statuses(my_user))

    // Update the newIncludeStatuses when something uopdates the includeStatuses
    React.useEffect(() => {
        setNewIncludeStatuses(JSON.parse(JSON.stringify(includeStatuses)))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [includeStatuses])

    return (
        <FormControl sx={{ mt: 1, minWidth: 300, width: '100%' }}>
            <InputLabel id="serviceorder-statuses-filter-checkbox-label">Service Order Status Filter</InputLabel>
            <Select
                labelId="serviceorder-statuses-filter-checkbox-label"
                id="serviceorder-statuses-filter-checkbox"
                multiple
                value={statuses.filter((x) => includeStatuses.includes(x))}
                onClose={(e) => {
                    if (JSON.stringify(newIncludeStatuses.sort()) !== JSON.stringify(includeStatuses.sort())) {
                        setIncludeStatuses(window.structuredClone(newIncludeStatuses))
                        localStorage.setItem(localStorageLocation, newIncludeStatuses)
                    }
                }}
                disabled={isLoading}
                input={<OutlinedInput fullWidth id="select-multiple-chip" label="Service Order Status Filter" />}
                renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                            <Chip key={value} label={<Box display={'flex'}>
                                <span>{value}</span>
                                {showServiceOrderStats &&
                                    <Box sx={{ paddingLeft: 1 }}>
                                        {serviceOrderStats ? serviceOrderStats[value] || 0 : <CircularProgress size={"15px"} />}
                                    </Box>
                                }
                            </Box>} color={get_service_order_color(my_user.client.service_order_statuses, value)} />
                        ))}
                    </Box>
                )}
            >
                <MenuItem value={'MAGIC_SELECT_ALL'} onClick={(e) => {
                    e.preventDefault();
                    setNewIncludeStatuses(newIncludeStatuses.length === statuses.length ? [] : [...statuses])
                }}>
                    <Checkbox
                        checked={newIncludeStatuses.length === statuses.length}
                        indeterminate={newIncludeStatuses.length > 0 && newIncludeStatuses.length < statuses.length}
                    />
                    <span><b>Select All</b></span>
                </MenuItem>
                {statuses.map((name) => (
                    <MenuItem key={name} value={name} onClick={(e) => {
                        e.preventDefault();
                        if (newIncludeStatuses.includes(name)) {
                            newIncludeStatuses.splice(newIncludeStatuses.indexOf(name), 1)
                        } else {
                            newIncludeStatuses.push(name)
                        }
                        setNewIncludeStatuses(window.structuredClone(newIncludeStatuses))
                    }}>
                        <Checkbox checked={newIncludeStatuses.includes(name)} />
                        <Chip label={<Box display={'flex'}>
                            <span>{name}</span>
                            {showServiceOrderStats &&
                                <Box sx={{ paddingLeft: 1 }}>
                                    {serviceOrderStats ? serviceOrderStats[name] || 0 : <CircularProgress size={"15px"} />}
                                </Box>
                            }
                        </Box>} color={get_service_order_color(my_user.client.service_order_statuses, name)} />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    )
}


export default function ServiceOrders(props) {
    DocumentTitle('Service Orders')
    const { enqueueSnackbar } = useSnackbar();
    const [my_user, set_my_user] = useTrackedUser();
    const { data: users, isLoadingUsers } = useUsers();
    const { data: locations, } = useLocations();
    const [paginationModel, setPaginationModel] = React.useState({
        pageSize: parseInt(localStorage.getItem(`serviceorders:pageSize`) || 10),
        page: 0,
    });
    const [selectedLocation,] = useTrackedLocation(); // REMOVED setSelectedLocation to make linter happy
    const [serviceOrderStats, setServiceOrderStats] = React.useState(undefined)
    const [rowData, setRowData] = React.useState({ rows: [], total_count: 0, first_render: true })
    const [sorting, setSorting] = React.useState({ field: 'date_created', sort: 'desc' })
    const [filtering, setFiltering] = React.useState({})
    const [isLoading, setIsLoading] = React.useState(true)
    const [statuses,] = React.useState(sort_custom_statuses(my_user.client.service_order_statuses))
    const [includeStatuses, setIncludeStatuses] = React.useState(localStorage.getItem(`serviceorders:status_filters`) ? localStorage.getItem(`serviceorders:status_filters`).split(',') : get_included_statuses(my_user))
    const [filteredAssignee, setFilteredAssignee] = React.useState(localStorage.getItem(`serviceorders:filtered_assignee`) ? JSON.parse(localStorage.getItem(`serviceorders:filtered_assignee`)) : null)
    const [serviceOrderSearch, setServiceOrderSearch] = React.useState('')
    const [filterStale, setFilterStale] = React.useState(false)
    const [filtersMenuAnchorElement, setFiltersMenuAnchorElement] = React.useState(null);
    const [windowInnerWidth, setWindowInnerWidth] = React.useState(window.innerWidth)
    const [customerSearch, setCustomerSearch] = React.useState(undefined)
    const [customerSearchModalOpen, setCustomerSearchModalOpen] = React.useState(false)
    const [inputModal, setInputModal] = React.useState({
        open: false,
        callback: null,
        label: '',
        title: '',
        defaultInput: ''
    })
    const [confirmModal, setConfirmModal] = React.useState({
        open: false,
        text: '',
        callback: null
    })
    const [madeUpdate, setMadeUpdate] = React.useState(false)
    const navigate = useNavigate();
    console.log("RENDERING ServiceOrders")

    const { serviceOrderId } = useParams();

    const my_serviceorder_views = my_user.user_extras.filter((x) => x.item_type === "SERVICEVIEW").sort((a, b) => a.data.name.localeCompare(b.data.name))

    const serviceOrdersRowClick = (data, event, details) => {
        if (event.ctrlKey) {
            window.open(`/ServiceOrders/${data.row.id}`)
        } else {
            navigate(`/ServiceOrders/${data.row.id}`)
        }
    }

    const setMyPaginationModel = (model) => {
        localStorage.setItem(`serviceorders:pageSize`, model.pageSize)
        setPaginationModel(model)
    }

    const updateServiceOrderSearch = (event) => {
        if (event.target.value.trim().match(/^\d*$/)) { // TODO: Question, prevent when isLoading?
            setServiceOrderSearch(event.target.value.trim())
            if (event.target.value.trim() !== serviceOrderSearch) {
                debouncedGetServiceOrders(getServiceOrders, paginationModel, sorting.field, sorting.sort, filtering, event.target.value.trim(), customerSearch)
            }
        }
    }

    const getServiceOrders = (paginationModel, sortField, sortDirection, filterVar, serviceOrderSearch, customerSearch) => {
        setIsLoading(true)
        let my_filters = { "and": [] }
        if (Object.keys(filterVar).length > 0) {
            my_filters["and"].push(filterVar)
        }
        if (customerSearch) {
            my_filters["and"].push({ 'customer_id.eq': customerSearch.id })
        }
        if (serviceOrderSearch.length > 0) {
            my_filters['and'].push({ 'id.eq': serviceOrderSearch })
        }
        if (selectedLocation.id !== "all") {
            my_filters['and'].push({
                "or": [
                    { 'location_id.eq': selectedLocation.id },
                    { 'current_location_id.eq': selectedLocation.id },
                ]
            })
        }
        if (filteredAssignee?.id) {
            if (filteredAssignee.id === -1) {
                my_filters['assignee_user_id.eq'] = null
            } else {
                my_filters['assignee_user_id.eq'] = filteredAssignee.id
            }
        }
        my_filters["and"].unshift({ 'or': [] })
        if (includeStatuses.length > 0 && includeStatuses.length !== statuses.length) {
            includeStatuses.forEach((status => {
                my_filters["and"][0]['or'].push({ 'status_name.eq': status })
            }))
        }
        if (filterStale) {
            my_filters["and"].unshift({ 'or': [] })
            Object.keys(my_user.client.service_order_statuses).forEach((status) => {
                if (my_user.client.service_order_statuses[status].alert_hours > 0) {
                    const stale_time = dayjs().subtract(my_user.client.service_order_statuses[status].alert_hours, 'hours').toISOString()
                    my_filters["and"][0]['or'].push({ 'and': [{ 'last_status_change.lte': stale_time }, { 'status_name.eq': status }] })
                }
            })
        }
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.get("serviceorders", {
                params: {
                    page_num: paginationModel.page,
                    page_size: paginationModel.pageSize,
                    order_by: sortField,
                    order_by_direction: sortDirection,
                    filters: JSON.stringify(my_filters),
                },
            }).then((serviceorders) => {
                setRowData({
                    rows: serviceorders.data.data.map((row) => {
                        return {
                            ...row,
                            actively_worked: row.service_order_work_history.some((entry) => entry.end_time === null),
                        }
                    }),
                    total_count: serviceorders.data.total_count,
                })
                setMadeUpdate(false)
                setIsLoading(false)
            }
            ).catch((error) => {
                enqueueSnackbar(`Failed to load service orders: ${error.response?.data?.message ?? "Unknown Error Occurred"}`, { variant: 'error' })
                setIsLoading(false)
            })
        )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedGetServiceOrders = React.useMemo(() => debounce((getServiceOrders, paginationModel, sortField, sortDirection, filterVar, serviceOrderSearch, customerSearch) => getServiceOrders(paginationModel, sortField, sortDirection, filterVar, serviceOrderSearch, customerSearch), 800), []);

    React.useEffect(() => {
        if (serviceOrderId === undefined && !rowData.first_render) {
            getServiceOrders(paginationModel, sorting.field, sorting.sort, filtering, serviceOrderSearch, customerSearch)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel, sorting, includeStatuses, filteredAssignee, filtering, filterStale])

    React.useEffect(() => {
        if (serviceOrderId === undefined && rowData.total_count === 0) {
            getServiceOrders(paginationModel, sorting.field, sorting.sort, filtering, serviceOrderSearch, customerSearch)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceOrderId])

    const getServiceOrderStats = () => {
        send_authorized_request(() => AXIOS_API_INSTANCE.get("serviceorders/stats", {
            params: {
                location_id: selectedLocation.id,
            }
        }).then((serviceorders_stats) => {
            setServiceOrderStats(serviceorders_stats.data.data)
        }).catch((error) => {
            enqueueSnackbar(`Failed to load service orders statistics: ${error.response?.data?.message ?? "Unknown Error Occurred"}`, { variant: 'error' })
            setServiceOrderStats({})
        })
        )
    }

    // React.useEffect(() => {
    //     if (serviceOrderStats === undefined) {
    //         getServiceOrderStats()
    //     }
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [])

    React.useEffect(() => {
        setServiceOrderStats(undefined)
        getServiceOrderStats()
        getServiceOrders(paginationModel, sorting.field, sorting.sort, filtering, serviceOrderSearch, customerSearch)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLocation])

    React.useEffect(() => {
        return widthChangeEvent(setWindowInnerWidth)
    }, [])

    return (
        (<React.Fragment>
            {serviceOrderId === undefined &&
                <Grid container space={1} sx={{ width: '100%' }}>
                    <Grid size="grow">
                        <Grid container columns={{ xs: 5, sm: 9, md: 9, lg: 14 }} columnSpacing={0} sx={{ mt: 0.5, mb: 0.5 }} direction="row" justifyContent="space-between" alignItems="center">
                            <Grid container size={4} columnSpacing={1}>
                                <Grid size="auto">
                                    <TextField
                                        margin="dense"
                                        id="contractsearch"
                                        label="Search Service Order ID"
                                        placeholder="####"
                                        type="number"
                                        variant="filled"
                                        value={serviceOrderSearch}
                                        onChange={updateServiceOrderSearch}
                                        onFocus={(e) => e.target.addEventListener('wheel', (e) => e.preventDefault())}
                                        sx={{
                                            minWidth: '150px', maxWidth: '175px',
                                            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                                display: "none",
                                            },
                                            "& input[type=number]": {
                                                MozAppearance: "textfield",
                                            },
                                        }}
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        {<SearchIcon />}
                                                    </InputAdornment>
                                                ),
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid size="auto" container direction={{ xs: 'column', sm: 'column', md: 'column', lg: 'column', xl: 'row' }} rowSpacing={2} alignItems={'center'}>
                                    <Grid>
                                        <Button variant="contained" onClick={() => setCustomerSearchModalOpen(true)} disabled={isLoading} startIcon={<SearchIcon />}>Customer</Button>
                                    </Grid>
                                    <Grid>
                                        {customerSearch && <Chip label={formatCustomerName(customerSearch) || 'NO NAME'} variant="outlined" onDelete={
                                            () => {
                                                setCustomerSearch(undefined)
                                                getServiceOrders(paginationModel, sorting.field, sorting.sort, filtering, serviceOrderSearch, undefined)
                                            }} />}
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid size={5}>
                                <center>
                                    <ServiceOrderStatusFilter
                                        statuses={statuses}
                                        includeStatuses={includeStatuses}
                                        setIncludeStatuses={setIncludeStatuses}
                                        serviceOrderStats={serviceOrderStats}
                                        isLoading={isLoading}
                                    />
                                </center>
                            </Grid>
                            <Grid size={3} justifyContent={'flex-end'}>
                                <Box display="flex" justifyContent="flex-end">
                                    <Autocomplete
                                        sx={{ color: 'rgba(0, 0, 0, 0.6)', maxWidth: '350px' }}
                                        getOptionLabel={(option) => `${option.name || ""}`}
                                        options={build_users_list(users, my_user, false, true, true, selectedLocation?.id ?? 'all')}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        autoComplete
                                        fullWidth
                                        includeInputInList
                                        // disableClearable
                                        value={filteredAssignee}
                                        noOptionsText="No Users"
                                        loading={isLoadingUsers}
                                        disabled={isLoading}
                                        onChange={(event, newValue) => {
                                            setFilteredAssignee(newValue)
                                            localStorage.setItem(`serviceorders:filtered_assignee`, JSON.stringify(newValue))
                                        }}
                                        slotProps={{
                                            popper: {
                                                sx: {
                                                    minWidth: '250px'
                                                }
                                            }
                                        }}
                                        renderInput={(params) => {
                                            let avatar = undefined
                                            if (params.inputProps["aria-expanded"]) {
                                                avatar = (
                                                    <span><UserAvatar user={{}} /></span>
                                                )
                                            } else {
                                                avatar = (
                                                    <span><UserAvatar user={(findUser(users, filteredAssignee?.id) ?? filteredAssignee) ?? {}} /></span>
                                                )
                                            }
                                            return (
                                                (<div style={{ position: "relative" }}>
                                                    <span
                                                        style={{
                                                            position: "absolute",
                                                            transform: "translateY(50%)",
                                                            marginLeft: "5px"
                                                        }}
                                                    >
                                                        {avatar}
                                                    </span>
                                                    <TextField
                                                        {...params}
                                                        label="Filter by Assignee"
                                                        fullWidth
                                                        autoComplete='off'
                                                        type="text"
                                                        slotProps={{
                                                            htmlInput: {
                                                                ...params.inputProps,
                                                                style: {
                                                                    padding: "4px 4px 0 32px",
                                                                    height: '36px'
                                                                }
                                                            },

                                                            inputLabel: {
                                                                shrink: true,
                                                                ...params.InputLabelProps,
                                                            }
                                                        }} />
                                                </div>)
                                            );
                                        }}
                                        renderOption={(props, option, { inputValue }) => {
                                            const matches = match(props.key, inputValue, { insideWords: true, findAllOccurrences: true });
                                            const parts = parse(props.key, matches);
                                            const li_props = { ...props }
                                            delete li_props.key

                                            return (
                                                (<li key={props.id} {...li_props}>
                                                    <Grid container space={1} alignItems="center">
                                                        <Grid sx={{ pr: 1 }} size="auto">
                                                            <span><UserAvatar user={option} /></span>
                                                        </Grid>
                                                        <Grid size="auto">
                                                            {parts.map((part, index) => (
                                                                <span
                                                                    key={index}
                                                                    style={{
                                                                        fontWeight: part.highlight ? 700 : 400,
                                                                    }}
                                                                >
                                                                    {part.text}
                                                                </span>
                                                            ))}
                                                        </Grid>
                                                    </Grid>
                                                </li>)
                                            );
                                        }}
                                    />
                                </Box>
                            </Grid>
                            <Grid size={2} justifySelf={'flex-end'}>
                                <Box sx={{ ml: 1, mr: 1, justifySelf: 'flex-end' }}>
                                    <Button variant="contained" onClick={(e) => setFiltersMenuAnchorElement(e.currentTarget)} disabled={isLoading} startIcon={<Badge badgeContent={(1 && filterStale) + 0} color="error">
                                        <FilterAltIcon />
                                    </Badge>}>
                                        Filters
                                    </Button>
                                </Box>
                                <Menu
                                    id={'serviceorders-filters-menu'}
                                    anchorEl={filtersMenuAnchorElement}
                                    open={Boolean(filtersMenuAnchorElement)}
                                    onClose={() => setFiltersMenuAnchorElement(null)}
                                    slotProps={{
                                        list: {
                                            'aria-labelledby': 'basic-button',
                                        }
                                    }}
                                >
                                    <MenuItem onClick={() => { setFilterStale(!filterStale); setFiltersMenuAnchorElement(null) }}>
                                        {filterStale ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                                        <Box sx={{ pl: 1 }}>Only Show Stale</Box>
                                    </MenuItem>
                                    <MenuItem onClick={() => {
                                        setFiltersMenuAnchorElement(null)
                                        setIncludeStatuses(get_included_statuses(my_user))
                                        setFilteredAssignee(null)
                                        setFilterStale(false)
                                        localStorage.setItem(`serviceorders:filtered_assignee`, JSON.stringify(null))
                                        localStorage.setItem(`serviceorders:status_filters`, get_included_statuses(my_user))
                                    }}>
                                        Reset Filters
                                    </MenuItem>
                                    <Divider />
                                    <ListSubheader>My Views</ListSubheader>
                                    {my_serviceorder_views.map((view) => {
                                        return (
                                            <MenuItem key={view.id} sx={{ justifyContent: 'space-between' }} onClick={() => {
                                                setFiltersMenuAnchorElement(null)
                                                setIncludeStatuses(view.data.statuses)
                                                const assignee = view.data.assignee_user_id === -1 ? { name: "Unassigned", id: -1 } : findUser(users, view.data.assignee_user_id)
                                                setFilteredAssignee(assignee)
                                                setFilterStale(view.data.filter_stale)
                                                localStorage.setItem(`serviceorders:filtered_assignee`, JSON.stringify(assignee ?? null))
                                                localStorage.setItem(`serviceorders:status_filters`, view.data.statuses)
                                            }}>
                                                {view.data.name}
                                                <IconButton sx={{ ml: 1, mr: -1 }} size="small" onClick={(e) => {
                                                    e.stopPropagation()
                                                    setConfirmModal({
                                                        open: true,
                                                        text: `Are you sure you want to delete your view "${view.data.name}"?`,
                                                        callback: (confirmation) => {
                                                            if (confirmation) {
                                                                setIsLoading(true)
                                                                send_authorized_request(() =>
                                                                    AXIOS_API_INSTANCE.delete(`my_user/extra/${view.id}`).then((response) => {
                                                                        enqueueSnackbar(`Successfully deleted view`, { variant: 'success' })
                                                                        setIsLoading(false)
                                                                        set_my_user({ ...my_user, user_extras: my_user.user_extras.filter((x) => x.id !== view.id) })
                                                                    }).catch((error) => {
                                                                        setIsLoading(false)
                                                                        enqueueSnackbar(`Failed to delete view <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                                                                    })
                                                                )
                                                            }
                                                            setConfirmModal({ ...confirmModal, open: false, text: `Are you sure you want to delete your view "${view.data.name}"?` })
                                                        }
                                                    })
                                                }}>
                                                    <DeleteIcon color="error" fontSize="small" />
                                                </IconButton>
                                            </MenuItem>
                                        )
                                    })}
                                    <MenuItem onClick={() => {
                                        setInputModal({
                                            ...inputModal,
                                            open: true,
                                            title: 'Save New View',
                                            label: 'Name',
                                            defaultInput: '',
                                            acceptEmpty: false,
                                            callback: (results, name) => {
                                                if (results) {
                                                    setFiltersMenuAnchorElement(null)
                                                    setIsLoading(true)
                                                    send_authorized_request(() =>
                                                        AXIOS_API_INSTANCE.put(`my_user/extra`, { item_type: 'SERVICEVIEW', data: { name: name, statuses: includeStatuses, assignee_user_id: filteredAssignee?.id ?? null, filter_stale: filterStale } }
                                                        ).then((extra) => {
                                                            enqueueSnackbar('Successfully created view', { variant: 'success' })
                                                            setIsLoading(false)
                                                            set_my_user({ ...my_user, user_extras: [...my_user.user_extras, extra.data.data] })
                                                        }).catch((error) => {
                                                            setIsLoading(false)
                                                            enqueueSnackbar(`Failed to create view <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error', autoHideDuration: 10000 })
                                                        })
                                                    )
                                                }
                                                setInputModal({ ...inputModal, open: false })
                                            }
                                        })
                                    }}>
                                        <AddCircleSharpIcon /><Box sx={{ pl: 1 }}>Save New View</Box>
                                    </MenuItem>
                                </Menu>
                            </Grid>
                        </Grid>
                        <DataGrid
                            key={windowInnerWidth}
                            slots={{
                                toolbar: () => SimpleCustomToolbar(),
                            }}
                            rows={rowData.rows}
                            rowCount={rowData.total_count}
                            getRowHeight={() => 'auto'}
                            columns={columns(my_user, users, locations)}
                            disableRowSelectionOnClick
                            experimentalFeatures={{ newEditingApi: true }}
                            autoHeight={true}
                            filterMode={"server"}
                            loading={isLoading}
                            pageSizeOptions={[5, 10, 25, 50, 100]}
                            pagination
                            paginationMode="server"
                            paginationModel={paginationModel}
                            onPaginationModelChange={setMyPaginationModel}
                            onRowClick={serviceOrdersRowClick}
                            sortingMode="server"
                            onSortModelChange={(params) => {
                                if (params[0] !== undefined) {
                                    setSorting(params[0])
                                } else {
                                    setSorting({ field: 'date_created', sort: 'desc' })
                                }
                            }}
                            onFilterModelChange={(params) => { setFiltering(onFilterChange(params)) }}
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        // columns set to false are hidden by default
                                        'customer_item:identifier': false,
                                        last_status_change: false,
                                        location_id: false,
                                        current_location_id: false,
                                        date_created: false,
                                    },
                                },
                            }}
                        />
                    </Grid>
                    <CustomerSearchModal
                        open={customerSearchModalOpen}
                        closeModal={(results) => {
                            setCustomerSearchModalOpen(false)
                            if (results) {
                                if (customerSearch?.id !== results.id) {
                                    setCustomerSearch(results)
                                    getServiceOrders(paginationModel, sorting.field, sorting.sort, filtering, serviceOrderSearch, results)
                                }
                            }
                        }}
                    />
                    <InputModal
                        {...inputModal}
                    />
                    <ConfirmModal
                        {...confirmModal}
                    />
                </Grid >}
            {
                serviceOrderId !== undefined &&
                <Box sx={{ width: '100%' }}>
                    <ServiceOrder
                        serviceOrderId={serviceOrderId}
                        setMadeUpdate={setMadeUpdate}
                        closeFunction={(update) => {
                            if (madeUpdate && !(rowData.total_count === 0)) { // We will always query if there are no rows. Make sure not to duplicate
                                getServiceOrders(paginationModel, sorting.field, sorting.sort, filtering, serviceOrderSearch, customerSearch)
                            }
                            navigate(`/ServiceOrders`)
                        }}
                    />
                </Box>
            }
        </React.Fragment >)
    );
}
